import Keycloak from 'keycloak-js';
import { env } from '@/utils/constants/env';

/**
 * Keycloak instance singleton
 * This instance is configured but NOT initialized on module load.
 * Call initKeycloak() to initialize it when needed.
 */
let keycloakInstance: Keycloak | null = null;

/**
 * BroadcastChannel for cross-tab communication
 */
let authBroadcastChannel: BroadcastChannel | null = null;

/**
 * Flag to prevent infinite loop in cross-tab sync
 */
let isProcessingLogout = false;

/**
 * Flag to track if Keycloak has been initialized
 * Keycloak can only be initialized once
 */
let isKeycloakInitialized = false;

/**
 * Promise to track ongoing initialization
 * Ensures only one initialization happens even with concurrent calls
 */
let initializationPromise: Promise<boolean> | null = null;

/**
 * Sync authentication state to localStorage for cross-tab detection
 * This is NOT the source of truth for tokens - Keycloak manages those internally
 * This is only a signal for other tabs to detect auth state changes
 */
const setAuthenticatedInStorage = (): void => {
  localStorage.setItem('keycloak-auth', 'true');
};
const clearAuthenticatedFromStorage = (): void => {
  localStorage.removeItem('keycloak-auth');
};

/**
 * Get or create Keycloak instance (without initialization)
 */
export const getKeycloakInstance = (): Keycloak => {
  keycloakInstance ??= new Keycloak({
    url: env.keycloakUrl,
    realm: env.keycloakRealm,
    clientId: env.keycloakClientId,
  });
  return keycloakInstance;
};

/**
 * Initialize Keycloak with PKCE flow
 * Should be called explicitly when authentication is needed
 * Returns true if authenticated, false otherwise
 */
export const initKeycloak = async (onAuthSuccess?: () => void, onAuthError?: (error: any) => void): Promise<boolean> => {
  const keycloak = getKeycloakInstance();

  // If initialization is already in progress, wait for it to complete
  if (initializationPromise !== null) {
    console.log('Keycloak initialization already in progress, waiting...');
    const authenticated = await initializationPromise;
    if (authenticated && onAuthSuccess) {
      onAuthSuccess();
    }
    return authenticated;
  }

  // If already initialized, return authentication status
  if (isKeycloakInitialized) {
    console.log('Keycloak already initialized, returning authentication status');
    const authenticated = keycloak.authenticated || false;
    if (authenticated && onAuthSuccess) {
      onAuthSuccess();
    }
    return authenticated;
  }

  // Start initialization and store the promise
  initializationPromise = (async () => {
    try {
      console.log('Initializing Keycloak with silent SSO check...');

      const authenticated = await keycloak.init({
        onLoad: 'check-sso', // Check SSO without forcing login
        pkceMethod: 'S256', // Use PKCE for security
        silentCheckSsoRedirectUri: globalThis.location.origin + '/silent-check-sso.html',
        silentCheckSsoFallback: true, // Fallback if silent check fails
        checkLoginIframe: true, // Enable iframe for better SSO detection
        checkLoginIframeInterval: 5, // Check every 5 seconds
        enableLogging: true, // Enable Keycloak logging for debugging
        // Let Keycloak handle token storage internally - no manual localStorage needed
      });

      console.log('Keycloak initialized. Authenticated:', authenticated);

      // Mark as initialized
      isKeycloakInitialized = true;

      if (authenticated) {
        console.log('User authenticated via SSO or existing session');

        // Sync auth state to localStorage for cross-tab detection only
        setAuthenticatedInStorage();

        // Setup token refresh
        setupTokenRefresh(keycloak);

        // Setup cross-tab synchronization
        setupCrossTabSync();

        if (onAuthSuccess) {
          onAuthSuccess();
        }
      } else {
        console.log('User not authenticated - SSO session not found');
        // Clear auth state from storage
        clearAuthenticatedFromStorage();
        // Still setup cross-tab sync even if not authenticated
        setupCrossTabSync();
      }

      return authenticated;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      isKeycloakInitialized = false; // Reset flag on error
      initializationPromise = null; // Allow retry on error
      if (onAuthError) {
        onAuthError(error);
      }
      return false;
    }
    // Note: We DON'T clear initializationPromise on success
    // This allows subsequent calls to await the same (already resolved) promise
  })();

  return initializationPromise;
};

/**
 * Setup automatic token refresh and SSO event handlers
 */
const setupTokenRefresh = (keycloak: Keycloak) => {
  // Handle token expiration
  keycloak.onTokenExpired = () => {
    console.log('Token expired, refreshing...');
    keycloak
      .updateToken(30) // Refresh if token expires in less than 30 seconds
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed successfully by Keycloak');
          // Keycloak manages tokens internally - no need to manually save
        }
      })
      .catch((error) => {
        console.error('Failed to refresh token:', error);
        // Clear auth state and redirect to login if refresh fails
        clearAuthenticatedFromStorage();
        keycloak.login();
      });
  };

  // Handle authentication success (including SSO)
  keycloak.onAuthSuccess = () => {
    console.log('Authentication successful');
    setAuthenticatedInStorage();
  };

  // Handle authentication error
  keycloak.onAuthError = () => {
    console.error('Authentication error occurred');
    clearAuthenticatedFromStorage();
  };

  // Handle token refresh success
  keycloak.onAuthRefreshSuccess = () => {
    console.log('Token refresh successful');
    // Token is managed by Keycloak internally
  };

  // Handle token refresh error
  keycloak.onAuthRefreshError = () => {
    console.warn('Token refresh error - may need to re-login');
    clearAuthenticatedFromStorage();
  };

  // Handle SSO logout (when logged out from another tab/app)
  keycloak.onAuthLogout = () => {
    console.log('User logged out via SSO');
    clearAuthenticatedFromStorage();
    // Clean up user data
    localStorage.removeItem('userProfile');
    localStorage.removeItem('auth-storage');
    // Redirect to login
    globalThis.location.href = '/login';
  };

  // Handle Keycloak ready event
  keycloak.onReady = (authenticated) => {
    console.log('Keycloak ready. Authenticated:', authenticated);
    if (authenticated ?? false) setAuthenticatedInStorage(); else clearAuthenticatedFromStorage();
  };
};

/**
 * Setup cross-tab synchronization for logout
 * This ensures when user logs out in one tab, all other tabs log out immediately
 */
const setupCrossTabSync = () => {
  // Prevent multiple setups
  if (authBroadcastChannel) {
    return;
  }

  try {
    // Use BroadcastChannel for modern browsers
    authBroadcastChannel = new BroadcastChannel('auth-channel');

    authBroadcastChannel.onmessage = (event) => {
      if (event.data.type === 'LOGOUT' && !isProcessingLogout) {
        console.log('Received logout message from another tab');
        isProcessingLogout = true;
        handleCrossTabLogout();
      }
    };

    console.log('Cross-tab sync initialized with BroadcastChannel');
  } catch (error) {
    console.warn('BroadcastChannel not supported, using storage event fallback', error);
    // Continue with storage event fallback below
  }

  // Also listen to storage events as a fallback
  // This works even if BroadcastChannel is not supported
  const handleStorageChange = (event: StorageEvent) => {
    // Detect when auth state is removed (logout)
    if (event.key === 'keycloak-auth' && event.oldValue && !event.newValue && !isProcessingLogout) {
      console.log('Detected logout via storage event from another tab');
      isProcessingLogout = true;
      handleCrossTabLogout();
    }

    // Also check for explicit logout signal
    if (event.key === 'logout-signal' && event.newValue === 'true' && !isProcessingLogout) {
      console.log('Detected logout signal from another tab');
      isProcessingLogout = true;
      handleCrossTabLogout();
    }
  };

  globalThis.addEventListener('storage', handleStorageChange);
  console.log('Cross-tab sync: storage event listener added');
};

/**
 * Handle logout triggered from another tab
 */
const handleCrossTabLogout = () => {
  console.log('Processing cross-tab logout...');

  // Clear auth state signal
  clearAuthenticatedFromStorage();

  // Clean up user data and persisted state
  localStorage.removeItem('userProfile');
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('current-tenant-storage');
  localStorage.removeItem('processes-storage');
  localStorage.removeItem('initial-tenant-id');

  // Reset Keycloak instance
  // Keycloak manages tokens internally, we just mark as unauthenticated
  if (keycloakInstance) {
    (keycloakInstance as any).authenticated = false;
    (keycloakInstance as any).token = undefined;
    (keycloakInstance as any).refreshToken = undefined;
    (keycloakInstance as any).idToken = undefined;
  }

  // Redirect to login page
  setTimeout(() => {
    isProcessingLogout = false;
    globalThis.location.href = '/login';
  }, 100);
};

/**
 * Notify other tabs that logout is happening
 */
const notifyOtherTabsLogout = () => {
  console.log('Notifying other tabs about logout...');

  // Use BroadcastChannel if available
  if (authBroadcastChannel) {
    try {
      authBroadcastChannel.postMessage({ type: 'LOGOUT' });
      console.log('Logout message broadcasted via BroadcastChannel');
    } catch (error) {
      console.warn('Failed to broadcast logout message:', error);
    }
  }

  // Also use localStorage as fallback (triggers storage event in other tabs)
  try {
    localStorage.setItem('logout-signal', 'true');
    // Remove it immediately so it can be set again next time
    setTimeout(() => {
      localStorage.removeItem('logout-signal');
    }, 100);
    console.log('Logout signal sent via localStorage');
  } catch (error) {
    console.warn('Failed to set logout signal in localStorage:', error);
  }
};

/**
 * Login with Keycloak
 */
export const loginWithKeycloak = async (redirectUri?: string): Promise<void> => {
  const keycloak = getKeycloakInstance();
  await keycloak.login({
    redirectUri: redirectUri || globalThis.location.origin + '/callback',
    scope: 'openid email profile',
  });
};

/**
 * Logout from Keycloak
 * This will redirect to Keycloak logout endpoint and invalidate the session
 */
export const logoutFromKeycloak = async (redirectUri: string): Promise<void> => {
  const keycloak = getKeycloakInstance();

  // Notify other tabs about logout BEFORE we clear tokens
  // This prevents race conditions
  if (!isProcessingLogout) {
    notifyOtherTabsLogout();
  }

  try {
    await keycloak.logout({
      redirectUri: redirectUri,
    });
  } catch (error) {
    console.error('Logout Failed', error);
    // Even if logout fails, redirect to the target page
    globalThis.location.href = redirectUri;
  }
};

/**
 * Get current access token (with auto-refresh if needed)
 * Keycloak handles token refresh internally, no manual localStorage needed
 */
export const getAccessToken = async (): Promise<string | undefined> => {
  // Wait for Keycloak initialization to complete
  const maxWaitTime = 10000; // 10 seconds max wait
  const startTime = Date.now();

  while (!isKeycloakInitialized && (Date.now() - startTime) < maxWaitTime) {
    // If init promise exists, wait for it
    if (initializationPromise !== null) {
      console.log('Waiting for ongoing Keycloak initialization...');
      try {
        await initializationPromise;
        break; // Init completed
      } catch (error) {
        console.error('Keycloak initialization failed:', error);
        return undefined;
      }
    }

    // Small delay before checking again
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  if (!isKeycloakInitialized) {
    console.log('Keycloak not initialized after waiting, returning undefined');
    return undefined;
  }

  const keycloak = getKeycloakInstance();

  if (!keycloak.authenticated) {
    console.log('Keycloak not authenticated, returning undefined token');
    return undefined;
  }

  try {
    // Update token if it's about to expire (within 30 seconds)
    await keycloak.updateToken(30);

    if (keycloak.token) {
      console.log('Token retrieved successfully');
      return keycloak.token;
    }
    console.log('Keycloak authenticated but token is undefined');
    return undefined;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return undefined;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const keycloak = getKeycloakInstance();
  return keycloak.authenticated ?? false;
};

/**
 * Check if Keycloak has been initialized
 */
export const isKeycloakReady = (): boolean => {
  return isKeycloakInitialized;
};

/**
 * Wait for Keycloak initialization to complete
 * Returns true if authenticated after init, false otherwise
 */
export const waitForKeycloakInit = async (): Promise<boolean> => {
  if (isKeycloakInitialized) {
    return isAuthenticated();
  }

  if (initializationPromise !== null) {
    return await initializationPromise;
  }

  // Not initialized and no init in progress
  return await initKeycloak();
};

/**
 * Cleanup resources (BroadcastChannel, event listeners)
 * Should be called when app is unmounting or no longer needs auth
 */
export const cleanupKeycloakResources = (): void => {
  if (authBroadcastChannel) {
    try {
      authBroadcastChannel.close();
      authBroadcastChannel = null;
      console.log('BroadcastChannel closed');
    } catch (error) {
      console.warn('Error closing BroadcastChannel:', error);
    }
  }
};
